import { put, call, select } from 'redux-saga/effects';
import Router from 'next/router';
import _ from 'lodash';
import api from '../utils/api';
import { getQuestions, getUsersAnswers } from '../reducers/questionnaire';
import { getUser } from '../reducers/user';
import {
  receiveFetchQuestionnaire,
  failureFetchQuestionnaire,
  receiveSendQuestionnaireAnswers,
  failureSenedQuestionnaireAnswers,
  receiveFetchQuestionnaireAnswers,
  failureFetchQuestionnaireAnswers,
} from '../actions/questionnaire';
import { receiveFetchAchievements } from '../actions/achievements';
import { restartProgramSuccess } from '../actions/programs';
import { showModal } from '../actions/modal';
import { fetchErrorHandler } from '../actions/fetchErrorHandler';
import { captureEvent } from '../utils/posthog';

// fetch questionnaire
export function* fetchQuestionnaire(action) {
  try {
    const { cookies } = yield select(getUser);
    const isProgramQuestionnaire = !!action.payload?.programId;
    const apiEndpoint = isProgramQuestionnaire
      ? `/questionnaire/programs/${action.payload.programId}`
      : `/questionnaire`;

    const { data } = yield call(api(cookies).get, apiEndpoint, action.payload);

    yield put(receiveFetchQuestionnaire(data));
  } catch (error) {
    yield put(fetchErrorHandler(error.response, 'Something went wrong'));
    yield put(failureFetchQuestionnaire('Something went wrong'));
  }
}

// send finished questionnaire by user
export function* sendQuestionnaireAnswers() {
  try {
    const { cookies } = yield select(getUser);
    const questions = yield select(getQuestions);
    const userAnswers = yield select(getUsersAnswers);

    const programQuestion = questions.find((q) => q.programs?.length);
    const programId = programQuestion?.programs[0].id;

    // filter questions with answers
    const filteredQuestions = questions.map((question) => {
      // find intersection between question answers and user answers
      const matchedAnswers = _.intersectionBy(question.answers, userAnswers[question.id], 'id');

      return { ...question, answers: matchedAnswers };
    });

    // filter questions with sub answers
    const filteredQuestionsSubAnswers = filteredQuestions.map((question) => {
      // get user subanswers for current question
      const userSubAnswers = userAnswers[question.id]
        ? userAnswers[question.id].reduce((acc, userAnswer) => {
            return userAnswer.subAnswers ? acc.concat(userAnswer.subAnswers) : acc;
          }, [])
        : [];

      const answers = question.answers.map((answer) => {
        // filter sub answers if its exists
        const filteredSubAnswers = answer.items
          ? answer.items.filter((item) => !!~userSubAnswers.indexOf(item.id))
          : null;

        // assign new created filtered subanswers array to answer item
        return { ...answer, items: filteredSubAnswers };
      });

      return { ...question, answers };
    });

    if (programId) {
      // Program (track) questionnaire: the API resolves the user from the session, assigns the
      // matching track and returns the updated user program, so we never depend on the numeric
      // user id being present in the store (it is not loaded on every path into this page).
      const { data: userProgram } = yield call(
        api(cookies).put,
        `/users/programs/${programId}/answers`,
        { questions: filteredQuestions },
      );

      yield put(receiveSendQuestionnaireAnswers(userProgram));
      yield put(restartProgramSuccess({ userPrograms: [userProgram], error: null }));
      captureEvent('questionnaire_completed', { questionnaire_type: 'program' });

      const nextSessionId = userProgram?.nextSession?.id;
      if (!nextSessionId) {
        throw new Error('Program has no next session after saving track answers');
      }

      const query = userProgram.nextTrackSessionId
        ? `?trackSessionId=${userProgram.nextTrackSessionId}`
        : '';
      Router.push(`/programs/${programId}/sessions/${nextSessionId}/start${query}`);
      return;
    }

    const { data } = yield call(api(cookies).post, '/questionnaire/user/answers', {
      questions: filteredQuestionsSubAnswers,
    });

    yield put(receiveSendQuestionnaireAnswers(data));
    captureEvent('questionnaire_completed', { questionnaire_type: 'general' });

    const { data: achievements } = yield call(api().get, `/users/achievements`);
    const notViewedAchievements = achievements.filter(
      (achievement) => achievement.viewed === false && achievement.earnedAt,
    );

    yield put(receiveFetchAchievements(achievements));

    Router.push('/').then(() => {
      // show achievements
      if (notViewedAchievements && notViewedAchievements.length) {
        put(showModal('ACHIEVEMENTS'));
      }
    });
  } catch (error) {
    yield put(failureSenedQuestionnaireAnswers("We couldn't save your answers. Please try again."));
    yield put(fetchErrorHandler(error.response, 'Error while sending questionnaire answers'));
  }
}

// fetch user questionnaire answers
export function* fetchUserQuestionnaireAnswers() {
  try {
    const { cookies } = yield select(getUser);

    const { data } = yield call(api(cookies).get, '/questionnaire/user/answers');

    yield put(receiveFetchQuestionnaireAnswers(data));
  } catch (error) {
    yield put(failureFetchQuestionnaireAnswers('Something went wrong'));
    yield put(fetchErrorHandler(error.response, 'Error while fetching questionnaire answers'));
  }
}
